/**
 * KAN-865: действия строки CRM (вынесено из CrmBoardTable, KAN-757/761/786):
 * у строки внимания "Успех", "Отказ", "Взять в работу" (менеджер сессии KAN-753
 * или модалка KAN-750), у отработанной только исход; набор кнопок даёт
 * rowActions, нажатие проставляет исход в кеше под queryKey сразу
 * (withCrmRowAction). Один источник логики для таблицы CRM и панели карточки
 * доски: render(item) рисует кнопки строки, dialogs - модалки "Взять в работу" и
 * "Успех" (рендерить один раз рядом).
 * KAN-880: "Успех"/"Отказ" рисуются одной кнопкой "Отработано" с выбором
 * (AttentionActionButtons); applyAction - форма кеша под queryKey (доска
 * CrmBoard по умолчанию, таблица лидов - applyLeadAction).
 */
import type { ReactNode } from 'react';
import { AttentionActionButtons } from '@/components/dashboard/AttentionActionButtons';
import { AttentionDoneDialog } from '@/components/dashboard/AttentionDoneDialog';
import {
  useAttentionActions,
  type ApplyAttentionAction,
  type AttentionAction,
} from '@/components/dashboard/useAttentionActions';
import { useAttentionDone } from '@/components/dashboard/useAttentionDone';
import { AttentionSuccessFlow } from '@/components/dashboard/AttentionSuccessDialog';
import { useAttentionSuccess } from '@/components/dashboard/useAttentionSuccess';
import { crmRowToAttentionItem, withCrmRowAction } from '@/lib/crmBoard';
import { rowActions, type CrmTableRow } from '@/lib/crmTable';
import type { WorkSurface } from '@/lib/workActions';
import type { CrmBoard } from '@/types/crm';

export interface CrmRowActions {
  /** Кнопки действий строки; нет действий: null. compact - иконки в одну строку (таблица). */
  render: (item: CrmTableRow, options?: { compact?: boolean }) => ReactNode;
  /** Модалки "Взять в работу" (назначение менеджера) и "Успех". */
  dialogs: ReactNode;
}

export function useCrmRowActions<T = CrmBoard>(
  queryKey: readonly unknown[],
  surface: WorkSurface = 'cabinet',
  applyAction: ApplyAttentionAction<T> = withCrmRowAction as unknown as ApplyAttentionAction<T>
): CrmRowActions {
  const actions = useAttentionActions<T>(queryKey, applyAction);
  const done = useAttentionDone(actions.run, surface);
  const success = useAttentionSuccess(actions.run);

  const onAction = (item: CrmTableRow, action: AttentionAction) => {
    if (action === 'done') done.request(crmRowToAttentionItem(item.row));
    else if (action === 'success') success.request(crmRowToAttentionItem(item.row));
    else actions.run(item.row.client_id, action);
  };

  const render = (item: CrmTableRow, { compact = true }: { compact?: boolean } = {}): ReactNode => {
    const available = rowActions(item);
    if (available.length === 0) return null;
    return (
      <AttentionActionButtons
        compact={compact}
        actions={available}
        disabled={actions.isPending(item.row.client_id) || success.isPending(item.row.client_id)}
        onAction={(action) => onAction(item, action)}
        doneLabel={done.label}
      />
    );
  };

  const dialogs = (
    <>
      <AttentionDoneDialog
        item={done.dialogItem}
        defaultManager={done.defaultManager}
        onConfirm={done.confirm}
        onCancel={done.cancel}
      />
      <AttentionSuccessFlow success={success} />
    </>
  );

  return { render, dialogs };
}
